import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import CustomButton from '../../../components/customButton';
import { useSelector } from 'react-redux';
import { getSolutionsConfig } from '../../../redux/presets/actions';
import DropdownSearch from '../../../components/dropdownSearch';
import MultiSelector from '../../../components/multiSelector';
import './styles.scss';
import utils from '../../../utils';
import API from '../../../services/api';

const SetPresetModal = ({ isOpen, devices, toggleModal }) => {
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const solutionsConfig = useSelector(getSolutionsConfig);

  const renderStatusTags = status => {
    const statusLegends = {
      success: 'Success',
      danger: 'Fail',
      secondary: 'Applying...'
    };

    // success, danger, info
    return <span className={`status badge badge-pill badge-outline-${status}`}>{statusLegends[status]}</span>;
  };

  const titles = {
    1: 'Apply a Preset',
    2: 'Confirm Changes',
    3: 'Presets Applied'
  };

  const bodyText = {
    1: 'Choose the preset you want to set for the selected devices.',
    2: `You are about to apply the preset to device(s). Are you sure you want to proceed?`,
    3: 'Check the changes on the devices list. It may take a few seconds for the changes to be reflected on the device status.'
  };

  const nextButtonText = {
    1: 'Next',
    2: 'Confirm',
    3: 'Close'
  };

  const cancelButtonText = {
    1: 'Cancel',
    2: 'Back',
    3: 'Close'
  };

  const buttonsDisabled = {
    1: !selectedPreset,
    2: false,
    3: isLoading
  };

  const modalBody = {
    1: (
      <DropdownSearch
        options={presets.map(preset => ({ label: preset.displayName, value: preset.id }))}
        onSelect={presetId => setSelectedPreset(presets.find(preset => preset.id === presetId))}
      />
    ),
    2: (
      <div>
        <div className="selected-preset">
          <label>Selected Preset:</label>
          <p>{selectedPreset?.displayName}</p>
        </div>
        <p>
          <strong>Devices to be updated:</strong>
        </p>
        <MultiSelector
          items={devices}
          renderSearchbox={false}
          renderCheckbox={false}
          displayField="displayName"
          valueField="id"
          listSize={120}
        />
      </div>
    ),
    3: (
      <div>
        <p>
          <strong>Applied Presets:</strong>
        </p>
        <MultiSelector
          items={devices}
          renderSearchbox={false}
          renderCheckbox={false}
          displayField="displayName"
          valueField="id"
          renderActionButtons={device => renderStatusTags(status[device.id])}
          listSize={120}
        />
      </div>
    )
  };

  useEffect(() => {
    setStep(1);
    devices.map(device => {
      setStatus(prevStatus => ({ ...prevStatus, [device.id]: 'secondary' }));
    });
  }, [isOpen]);

  const handleApplyPreset = () => {
    setIsLoading(true);

    const applyPresetPromises = devices.map(device => {
      const deviceCopy = utils.deepClone(device);
      let fields = ['customConfig', 'defaultSolutionConfigId'];
      let parsedData = {
        customConfig: selectedPreset.config,
        name: deviceCopy.name,
        defaultSolutionConfigId: selectedPreset.id
      };

      return API.updateDeviceConfig(deviceCopy.projectId, deviceCopy.id, parsedData, fields);
    });

    Promise.allSettled(applyPresetPromises)
      .then(results => {
        results.forEach((res, idx) => {
          const deviceId = devices[idx].id;
          if (res.status === 'fulfilled') {
            setStatus(prevStatus => ({ ...prevStatus, [deviceId]: 'success' }));
          } else {
            setStatus(prevStatus => ({ ...prevStatus, [deviceId]: 'danger' }));
            console.error(`Error applying preset to device ${deviceId}:`, res.reason);
          }
        });
      })
      .catch(error => {
        console.error('Error applying preset to devices:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNextButton = () => {
    if (step === 1 && selectedPreset) {
      setStep(2);
    } else if (step === 2) {
      handleApplyPreset();
      setStep(3);
    } else if (step === 3) {
      toggleModal();
      setStep(1);
      setSelectedPreset(null);
    }
  };

  const handleCancelButton = () => {
    if (step === 1) {
      toggleModal();
    } else if (step === 2) {
      setStep(1);
      setSelectedPreset(null);
    } else if (step === 3) {
      toggleModal();
      setStep(1);
      setSelectedPreset(null);
    }
  };

  useEffect(() => {
    const presetList = updatePresetsList(solutionsConfig);
    setPresets(presetList);
  }, [solutionsConfig]);

  const updatePresetsList = newSolutionsConfig => {
    const solutionsConfigList = Object.keys(solutionsConfig)
      .map(key => newSolutionsConfig[key])
      .reduce((prev, curr) => [...prev, ...curr], []);
    return [...solutionsConfigList];
  };

  return (
    <Modal size="md" isOpen={isOpen} fade={false} toggle={toggleModal} centered id="set-preset-modal">
      <ModalHeader toggle={toggleModal}>{titles[step]}</ModalHeader>
      <ModalBody>
        <p>{bodyText[step]}</p>
        {modalBody[step]}
      </ModalBody>
      <ModalFooter>
        {step !== 3 && (
          <CustomButton classes="btn btn-secondary" title={cancelButtonText[step]} handleClick={handleCancelButton} />
        )}
        <CustomButton
          classes="btn btn-primary"
          title={nextButtonText[step]}
          handleClick={handleNextButton}
          disabled={buttonsDisabled[step]}
        />
      </ModalFooter>
    </Modal>
  );
};

export default SetPresetModal;
